import { Button } from '@today/ui-primitives'
import { useState } from 'react'
import { type FieldErrors, NumberInput, TextInput } from '../file-exchange-file-form/form-fields'

/** A new connection as the add form submits it. */
export interface SftpConnectionDraft {
	name: string
	host: string
	port: number
	username: string
}

const EMPTY: SftpConnectionDraft = { name: '', host: '', port: 22, username: '' }

/** Props for {@link SftpConnectionForm}. */
export interface SftpConnectionFormProps {
	busy: boolean
	errors: FieldErrors
	error?: string | null
	onSubmit: (draft: SftpConnectionDraft) => void
	onCancel: () => void
}

/**
 * The "Add connection" form (TO-393 SCN-014): name, host, port and username —
 * never a password or a key (TODAY generates the key pair). Field errors land
 * next to their field by API path; the draft is kept when a save is refused.
 */
export function SftpConnectionForm({ busy, errors, error, onSubmit, onCancel }: SftpConnectionFormProps) {
	const [draft, setDraft] = useState<SftpConnectionDraft>(EMPTY)
	const set =
		<K extends keyof SftpConnectionDraft>(key: K) =>
		(value: SftpConnectionDraft[K]) =>
			setDraft((current) => ({ ...current, [key]: value }))
	return (
		<form
			className="flex flex-col gap-3 rounded-ds-md border border-ds-border p-ds-4"
			onSubmit={(event) => {
				event.preventDefault()
				onSubmit(draft)
			}}
		>
			<TextInput label="Name" path="name" errors={errors} value={draft.name} onChange={set('name')} />
			<TextInput label="Host" path="host" errors={errors} value={draft.host} onChange={set('host')} />
			<NumberInput label="Port" path="port" errors={errors} value={draft.port} onChange={set('port')} />
			<TextInput label="Username" path="username" errors={errors} value={draft.username} onChange={set('username')} />
			{error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}
			<div className="flex gap-2">
				<Button type="submit" isDisabled={busy}>
					Save connection
				</Button>
				<Button variant="muted" isDisabled={busy} onPress={onCancel}>
					Cancel
				</Button>
			</div>
		</form>
	)
}
